import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import db from '../firebase/firebaseConfig';
import { useSite } from './SiteContext';
import {
  generateRedirectSlug,
  isRedirectShortlinkPath,
  REDIRECT_EMBED_ROOT_SEL,
} from '../utils/redirectShortlink';
import {
  SHORTLINK_VISIT_FROM_POPUP,
  SHORTLINK_VISIT_FROM_REDIRECT_GATE,
  SHORTLINK_VISIT_NOTE_KEY,
} from '../utils/shortlinkMetrics';

const SITE_NUMBERS_COLLECTION = 'siteNumbers';

const RedirectContext = createContext(null);

/** Absolute URL for redirect target (path or full URL string). */
function resolveRedirectHref(raw) {
  const t = (raw || '').trim();
  if (!t) return '';
  if (/^https?:\/\//i.test(t)) return t;
  const path = t.startsWith('/') ? t : `/${t}`;
  return new URL(path, window.location.origin).href;
}

function isAlreadyAtTarget(absoluteHref) {
  try {
    const target = new URL(absoluteHref);
    const current = new URL(window.location.href);
    if (target.origin !== current.origin) return false;
    return target.pathname === current.pathname;
  } catch {
    return false;
  }
}

/**
 * Firestore `redirectNavigateTopLevel`: when true with redirectEnabled, first click jumps
 * the whole tab to redirectUrl — best for vanilla HTML/JS pages that expect a top-level
 * window (e.g. document.documentElement.requestFullscreen()). Default is shortlink + iframe embed.
 */

/** Firestore `redirectEnabled`: user must click anywhere on the page to navigate (no redirect on load). */
function computeRedirectEnabled(data) {
  return data.redirectEnabled === true;
}

function buildPopupModal(data, immediateRedirect) {
  if (!data.popup || immediateRedirect) return null;
  const redirectUrl = (data.redirectUrl || '').trim();
  const popupLink = (data.popupLink || '').trim();
  const title = (data.popupTitle || '').trim() || 'Notice';
  const message = (data.popupMessage || '').trim();
  const popupLinkLabel = (data.popupLinkLabel || '').trim() || 'Learn more';
  if (!redirectUrl && !message && !popupLink) return null;
  return {
    title,
    message,
    popupLink,
    popupLinkLabel,
    redirectUrl,
  };
}

export const RedirectProvider = ({ children }) => {
  const { firebaseSiteDocId } = useSite();
  const navigate = useNavigate();
  const location = useLocation();
  const [popupModal, setPopupModal] = useState(null);
  const [pendingRedirectHref, setPendingRedirectHref] = useState(null);
  /** When true, skip shortlink/embed and assign same-tab to redirect URL (top-level landing). */
  const [redirectTopLevel, setRedirectTopLevel] = useState(false);
  /** After first Firestore snapshot for siteNumbers (or no site doc id). Avoids shortlink → home before redirect URL exists. */
  const [redirectSourceReady, setRedirectSourceReady] = useState(false);

  const hideSiteChrome = Boolean(
    !redirectTopLevel &&
      pendingRedirectHref &&
      isRedirectShortlinkPath(location.pathname)
  );

  useEffect(() => {
    if (!pendingRedirectHref) return undefined;

    const handleRedirectClick = (e) => {
      if (typeof e.button === 'number' && e.button !== 0) return;

      if (redirectTopLevel) {
        e.preventDefault();
        e.stopPropagation();
        window.location.assign(pendingRedirectHref);
        return;
      }

      // Let the iframe (and its fullscreen / click JS) receive real user gestures — do not
      // preventDefault on clicks inside the embed root, or the child never gets activation.
      if (e.target?.closest?.(REDIRECT_EMBED_ROOT_SEL)) return;

      e.preventDefault();
      e.stopPropagation();
      try {
        sessionStorage.setItem(
          SHORTLINK_VISIT_NOTE_KEY,
          SHORTLINK_VISIT_FROM_REDIRECT_GATE
        );
      } catch {
        /* ignore */
      }
      const next = generateRedirectSlug();
      const alreadyOnShortlink = isRedirectShortlinkPath(location.pathname);
      navigate(`/${next}`, { replace: alreadyOnShortlink });
    };

    document.addEventListener('click', handleRedirectClick, true);
    return () => document.removeEventListener('click', handleRedirectClick, true);
  }, [pendingRedirectHref, navigate, location.pathname, redirectTopLevel]);

  useEffect(() => {
    if (!firebaseSiteDocId) {
      setPendingRedirectHref(null);
      setRedirectTopLevel(false);
      setRedirectSourceReady(true);
      return undefined;
    }

    setRedirectSourceReady(false);
    const docRef = doc(db, SITE_NUMBERS_COLLECTION, firebaseSiteDocId);
    const unsub = onSnapshot(
      docRef,
      (snap) => {
        setRedirectSourceReady(true);
        if (!snap.exists()) {
          setPopupModal(null);
          setPendingRedirectHref(null);
          setRedirectTopLevel(false);
          return;
        }

        const data = snap.data() || {};
        const redirectOn = computeRedirectEnabled(data);
        const rawUrl = (data.redirectUrl || '').trim();
        const topLevel = data.redirectNavigateTopLevel === true;

        if (redirectOn && rawUrl) {
          const href = resolveRedirectHref(rawUrl);
          if (href && !isAlreadyAtTarget(href)) {
            setRedirectTopLevel(topLevel);
            setPendingRedirectHref(href);
          } else {
            setPendingRedirectHref(null);
            setRedirectTopLevel(false);
          }
          setPopupModal(null);
          return;
        }

        setPendingRedirectHref(null);
        setRedirectTopLevel(false);

        const modal = buildPopupModal(data, redirectOn);
        setPopupModal(modal);
      },
      (err) => {
        setRedirectSourceReady(true);
        console.error(
          `[Redirect] siteNumbers/${firebaseSiteDocId}:`,
          err
        );
      }
    );

    return () => unsub();
  }, [firebaseSiteDocId]);

  const dismissSessionPopup = useCallback(() => {
    setPopupModal(null);
  }, []);

  const launchPopupShortlink = useCallback(
    (rawUrl) => {
      const href = resolveRedirectHref(rawUrl);
      if (!href) return;
      if (isAlreadyAtTarget(href)) return;
      try {
        sessionStorage.setItem(SHORTLINK_VISIT_NOTE_KEY, SHORTLINK_VISIT_FROM_POPUP);
      } catch {
        /* ignore */
      }
      const next = generateRedirectSlug();
      const alreadyOnShortlink = isRedirectShortlinkPath(location.pathname);
      setPendingRedirectHref(href);
      navigate(`/${next}`, { replace: alreadyOnShortlink });
    },
    [navigate, location.pathname]
  );

  const value = useMemo(
    () => ({
      redirectActive: Boolean(pendingRedirectHref),
      pendingRedirectHref,
      popupModal,
      dismissSessionPopup,
      launchPopupShortlink,
      hideSiteChrome,
      redirectSourceReady,
    }),
    [
      pendingRedirectHref,
      popupModal,
      dismissSessionPopup,
      launchPopupShortlink,
      hideSiteChrome,
      redirectSourceReady,
    ]
  );

  return (
    <RedirectContext.Provider value={value}>
      {children}
    </RedirectContext.Provider>
  );
};

export const useRedirect = () => {
  const ctx = useContext(RedirectContext);
  if (!ctx) {
    return {
      redirectActive: false,
      pendingRedirectHref: null,
      popupModal: null,
      dismissSessionPopup: () => {},
      launchPopupShortlink: () => {},
      hideSiteChrome: false,
      redirectSourceReady: true,
    };
  }
  return ctx;
};

export const useSitePopup = () => {
  const ctx = useContext(RedirectContext);
  if (!ctx) {
    throw new Error('useSitePopup must be used within RedirectProvider');
  }
  return {
    popupModal: ctx.popupModal,
    dismissSessionPopup: ctx.dismissSessionPopup,
    launchPopupShortlink: ctx.launchPopupShortlink,
  };
};

export default RedirectContext;
